"use client";

import { useRef, useState } from "react";
import Link from "next/link";
import { cn } from "@/lib/utils";
import Image from "next/image";
import hasFadeAnim from "@/lib/animation/hasFadeAnim";
import { useGSAP } from "@gsap/react";
import siteConfig from "@/config/siteConfig.json";

type CardId = string | number;

type Card = {
  id: CardId;
  title: string;
  description: string;
  link: string;
  bgImage: string;
};

interface Props {
  businessUnits: Card[];
}

const ExpandingCards = ({ businessUnits }: Props) => {
  const cards = businessUnits;
  const containerRef = useRef<HTMLDivElement>(null!);

  useGSAP(
    () => {
      hasFadeAnim();
    },
    { scope: containerRef },
  );

  const [hoveredId, setHoveredId] = useState<CardId | null>(null);
  const [activeId, setActiveId] = useState<CardId | null>(null); // mobile tap
  const [expandedId, setExpandedId] = useState<CardId | null>(null); // desktop: fully expanded
  const expandTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { footer_info } = siteConfig;

  const handleMouseEnter = (id: CardId) => {
    setHoveredId(id);
    // wait for flex transition to finish before allowing text wrap
    if (expandTimerRef.current) clearTimeout(expandTimerRef.current);
    expandTimerRef.current = setTimeout(() => setExpandedId(id), 520);
  };

  const handleMouseLeave = () => {
    setHoveredId(null);
    setExpandedId(null);
    if (expandTimerRef.current) clearTimeout(expandTimerRef.current);
  };

  return (
    <section ref={containerRef}>
      {/* ── MOBILE: vertical stack (< md) ── */}
      <div className="inner-container md:hidden">
        <div className="section-spacing-top">
          <div className="flex flex-col gap-3">
            {cards?.map((card) => {
              const isActive = activeId === card.id;
              return (
                <div
                  key={card.id}
                  className="relative overflow-hidden cursor-pointer"
                  style={{
                    height: isActive ? "340px" : "220px",
                    transition: "height 0.5s ease",
                  }}
                  onClick={() => setActiveId(isActive ? null : card.id)}
                  // onMouseEnter={() => setActiveId(card.id)}
                >
                  {/* Background */}
                  <div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{ backgroundImage: `url(${card.bgImage})` }}
                  />
                  {/* Overlay */}
                  <div className="absolute inset-0 bg-black/45" />

                  {/* Logo */}
                  <div className="absolute top-4 left-4 z-10">
                    <Image
                      src={footer_info.logo}
                      width={110}
                      height={30}
                      alt={footer_info.logo_text}
                      className="h-auto"
                    />
                  </div>

                  {/* Watermark title */}
                  <div className="absolute inset-0 top-[20%] overflow-hidden z-10 px-4">
                    <h2
                      className={cn(
                        "text-[60px] sm:text-[100px] leading-none font-normal select-none transition-all duration-500",
                        isActive
                          ? "text-white whitespace-normal break-words"
                          : "text-transparent whitespace-nowrap",
                      )}
                      style={{ WebkitTextStroke: "1px rgba(255,255,255,0.45)" }}
                    >
                      {card.title}
                    </h2>
                  </div>

                  {/* Bottom content — always visible on mobile */}
                  <div className="absolute bottom-0 left-0 right-0 p-5 z-20">
                    <p
                      className={cn(
                        "text-white text-[13px] leading-relaxed mb-4 line-clamp-6 transition-all duration-500",
                        isActive
                          ? "opacity-100 translate-y-0"
                          : "opacity-0 translate-y-4",
                      )}
                    >
                      {card.description}
                    </p>
                    <Link
                      href={card.link}
                      aria-label={`Learn more about ${card.title}`}
                      className="inline-flex items-center justify-center px-5 py-2.5 border border-white rounded-full text-white text-[12px] tracking-wide hover:bg-white hover:text-black transition-all duration-300"
                    >
                      Read More
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── DESKTOP: horizontal expanding (md+) ── */}
      <div
        className="mx-auto w-full hidden md:block"
        style={{
          maxWidth: hoveredId ? "100%" : "var(--container-max-widths)",
          paddingLeft: hoveredId ? "0px" : "12px",
          paddingRight: hoveredId ? "0px" : "12px",
          transition:
            "max-width 600ms cubic-bezier(0.4, 0, 0.2, 1), padding 600ms cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        <div className="section-spacing-top">
          <div className="flex min-h-[550px] max-h-[900px] h-[90vh] overflow-hidden gap-1">
            {cards?.map((card) => {
              const isHovered = hoveredId === card.id;
              const anyHovered = hoveredId !== null;

              return (
                <div
                  key={card.id}
                  className={cn(
                    "relative overflow-hidden cursor-pointer transition-all duration-500 ease-in-out",
                    isHovered
                      ? "flex-[2.5]"
                      : anyHovered
                        ? "flex-[0.75]"
                        : "flex-[1]",
                  )}
                  onMouseEnter={() => handleMouseEnter(card.id)}
                  onMouseLeave={() => handleMouseLeave()}
                >
                  {/* Background image */}
                  <div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{ backgroundImage: `url(${card.bgImage})` }}
                  />

                  {/* Dark overlay */}
                  <div
                    className={cn(
                      "absolute inset-0 transition-all duration-500",
                      isHovered ? "bg-black/30" : "bg-black/50",
                    )}
                  />

                  {/* Logo — top left */}
                  <div className="absolute top-6 left-6 z-10">
                    <Image
                      src={footer_info.logo}
                      width={footer_info.logo_width}
                      height={footer_info.logo_height}
                      alt={footer_info.logo_text}
                      className="h-auto"
                    />
                  </div>

                  {/* Large watermark title */}
                  <div className="absolute inset-0 top-[20%] overflow-hidden z-10 px-8">
                    <h2
                      className={cn(
                        "text-[100px] lg:text-[120px] xl:text-[160px] leading-none font-normal select-none transition-all duration-500",
                        isHovered
                          ? expandedId === card.id
                            ? "text-white whitespace-normal break-words"
                            : "text-white whitespace-nowrap"
                          : "text-transparent whitespace-nowrap",
                      )}
                      style={{
                        WebkitTextStroke: isHovered
                          ? "0px transparent"
                          : "1px rgba(255,255,255,0.45)",
                      }}
                    >
                      {card.title}
                    </h2>
                  </div>

                  {/* Bottom content */}
                  <div className="absolute bottom-0 left-0 right-0 p-8 z-20">
                    <p
                      className={cn(
                        "text-white text-[15px] leading-relaxed mb-6"
                      )}
                    >
                      {card.description}
                    </p>
                    <Link
                      href={card.link}
                      aria-label={`Learn more about ${card.title}`}
                      className="inline-flex items-center justify-center px-8 py-4 border border-white rounded-full text-white text-[16px] tracking-wide hover:bg-white hover:text-black transition-all duration-300"
                    >
                      Read More
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ExpandingCards;

// const ExpandingCards = () => {
//   const containerRef = useRef<HTMLDivElement>(null!);

//   useGSAP(
//     () => {
//       hasFadeAnim();
//     },
//     { scope: containerRef }
//   );

//   const [hoveredId, setHoveredId] = useState<string | null>(null);
//   const { footer_info, social } = siteConfig;

//   return (
//     <section ref={containerRef}>
//       <div className="inner-container">
//         <div className="main-section-spacing">
//           <div className="w-full flex h-[90vh] overflow-hidden gap-2">
//             {cards.map((card) => {
//               const isHovered = hoveredId === card.id;
//               const anyHovered = hoveredId !== null;

//               return (
//                 <div
//                   key={card.id}
//                   className={cn(
//                     "relative overflow-hidden cursor-pointer transition-all duration-500 ease-in-out",
//                     isHovered
//                       ? "flex-[2.5]"
//                       : anyHovered
//                       ? "flex-[0.75]"
//                       : "flex-[1]"
//                   )}
//                   onMouseEnter={() => setHoveredId(card.id)}
//                   onMouseLeave={() => setHoveredId(null)}
//                 >
//                   {/* Background image */}
//                   <div
//                     className="absolute inset-0 bg-cover bg-center transition-transform duration-700 ease-in-out"
//                     style={{
//                       backgroundImage: `url(${card.bgImage})`,
//                       transform: isHovered ? "scale(1)" : "scale(1)",
//                     }}
//                   />

//                   {/* Dark overlay — lighter when hovered */}
//                   <div
//                     className={cn(
//                       "absolute inset-0 transition-all duration-500",
//                       isHovered ? "bg-black/30" : "bg-black/50"
//                     )}
//                   />

//                   {/* TIGAR brand label — top left */}
//                   <div className="absolute top-6 left-6 z-10">
//                     {/* <span className="text-white text-xs font-light tracking-[0.3em] uppercase">
//                       TIGAR
//                     </span> */}
//                     <div className="flex-shrink-0">
//                       <Link href="/">
//                         <Image
//                           src={footer_info.logo}
//                           width={footer_info.logo_width}
//                           height={footer_info.logo_height}
//                           alt={footer_info.logo_text}
//                           className="h-auto"
//                         />
//                       </Link>
//                     </div>
//                   </div>

//                   {/* Large watermark title */}
//                   <div className="absolute inset-0 flex items-center overflow-hidden z-10">
//                     <h2
//                       className={cn(
//                         "text-[100px] md:text-[140px] xl:text-[180px] leading-none font-light whitespace-nowrap select-none transition-all duration-500",
//                         isHovered
//                           ? "text-white"
//                           : "text-transparent"
//                       )}
//                       style={{
//                         WebkitTextStroke: isHovered
//                           ? "0px transparent"
//                           : "1px rgba(255,255,255,0.45)",
//                         paddingLeft: "0.5rem",
//                       }}
//                     >
//                       {card.title}
//                     </h2>
//                   </div>

//                   {/* Bottom content */}
//                   <div
//                     className={cn(
//                       "absolute bottom-0 left-0 right-0 p-6 md:p-8 z-20 transition-all duration-500 r",
//                       isHovered ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
//                     )}
//                   >
//                     <p className="text-white text-[14px] md:text-[15px] leading-relaxed mb-6 max-full">
//                       {card.description}
//                     </p>
//                     <Link
//                       href={card.link}
//                       className="inline-flex items-center justify-center px-6 py-3 border border-white rounded-full text-white text-[13px] tracking-wide hover:bg-white hover:text-black transition-all duration-300"
//                     >
//                       Read More
//                     </Link>
//                   </div>
//                 </div>
//               );
//             })}
//           </div>
//         </div>
//       </div>
//     </section>
//   );
// };
